import React, { useState } from 'react';
import Card from '../../common/Card';
import Button from '../../common/Button';
import SearchInput from '../../common/SearchInput';
import Loading from '../../common/Loading';
import ErrorMessage from '../../common/ErrorMessage';
import WarrantyCard from '../WarrantyCard';
import useWarranty from '../../../hooks/useWarranty';
import './styles.css';

/**
 * 保養單列表組件
 * 用於顯示和管理保養單
 * 
 * @param {Object} props - 組件屬性
 * @param {string} props.token - 認證令牌
 * @param {Object} props.user - 用戶信息
 * @returns {JSX.Element} 保養單列表組件
 */
function WarrantyList({ token, user }) {
    // 使用自定義Hook
    const {
        loading,
        error,
        updateError,
        searchTerm,
        setSearchTerm,
        filterStatus,
        setFilterStatus,
        fetchWarranties,
        groupedWarranties,
        formatDate
    } = useWarranty(token);

    // 加載中狀態
    if (loading) {
        return <Loading text="正在加載保養單數據..." />;
    }

    return (
        <div className="warranty-list-container">
            {/* 頁面標題 */}
            <div className="warranty-list-header">
                <h2 className="warranty-list-title">保養單管理</h2>
            </div>

            {/* 搜索和過濾 */}
            <div className="warranty-list-controls">
                <div className="warranty-list-search">
                    <SearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        placeholder="搜索保養單..."
                    />
                </div>

                <div className="warranty-list-filters">
                    <Button
                        variant={filterStatus === 'Pending' ? 'primary' : 'secondary'}
                        onClick={() => setFilterStatus('Pending')}
                    >
                        待驗證 ({groupedWarranties.Pending.length})
                    </Button>
                    <Button
                        variant={filterStatus === 'Approved' ? 'primary' : 'secondary'}
                        onClick={() => setFilterStatus('Approved')}
                    >
                        已通過 ({groupedWarranties.Approved.length})
                    </Button>
                    <Button
                        variant={filterStatus === 'Rejected' ? 'primary' : 'secondary'}
                        onClick={() => setFilterStatus('Rejected')}
                    >
                        已拒絕 ({groupedWarranties.Rejected.length})
                    </Button>
                </div>
            </div>

            {/* 錯誤信息 */}
            {error && (
                <ErrorMessage
                    message={error}
                    onRetry={fetchWarranties}
                />
            )}

            {updateError && (
                <ErrorMessage
                    message={updateError}
                />
            )}

            {/* 保養單列表 */}
            <div className="warranty-list-section">
                <Card className="warranty-list-card">
                    <h3 className="warranty-list-section-title">
                        {filterStatus === 'Pending' && '待驗證保養單'}
                        {filterStatus === 'Approved' && '已通過保養單'}
                        {filterStatus === 'Rejected' && '已拒絕保養單'}
                        <span className="warranty-list-count">
                            ({groupedWarranties[filterStatus].length})
                        </span>
                    </h3>

                    {groupedWarranties[filterStatus].length === 0 ? (
                        <div className="warranty-list-empty">
                            <p>暫無{filterStatus === 'Pending' ? '待驗證' : filterStatus === 'Approved' ? '已通過' : '已拒絕'}保養單</p>
                        </div>
                    ) : (
                        <div className="warranty-list-grid">
                            {groupedWarranties[filterStatus].map((warranty) => (
                                <WarrantyCard
                                    key={warranty.id}
                                    warranty={warranty}
                                    formatDate={formatDate}
                                />
                            ))}
                        </div>
                    )}
                </Card>
            </div>
        </div>
    );
}

export default WarrantyList; 